/**
 * Modal de detalhe de caixa de picking.
 *
 * Exibe cabeçalho da caixa (status, lacre, usuários, timestamps, notas)
 * e grade de itens (linha, SKU, produto, lote, quantidade, unidade, embalagem).
 * Permite cancelar caixa quando aplicável.
 */

import api from '../api.js';
import dialog from '../../versa/dialog.js';
import { notify } from '../../versa/msgbox.js';
import {
	STATUS_BOX_LABEL,
	STATUS_BOX_STYLE,
	escHtml,
	fmtDt,
	openCancelBoxModal,
	openAddItemsModal,
	openCloseBoxModal,
} from '../utils/output-picking-box-ops.js';

function buildItemsTable(items, users) {
	if (!items || !items.length) {
		return '<p style="color:#888;font-style:italic;margin:0.5rem 0">Nenhum item registrado nesta caixa.</p>';
	}
	const rows = items.map(item => {
		const createdByName = item.created_by_username || (users || {})[item.created_by] || item.created_by || '—';
		return `<tr>
			<td>${escHtml(item.output_item_number)}</td>
			<td>${escHtml(item.sku || '—')}</td>
			<td>${escHtml(item.product_name || item.product_id || '—')}</td>
			<td>${escHtml(item.lot_code || (item.lot_id ? `#${item.lot_id}` : '—'))}</td>
			<td style="text-align:right">${escHtml(item.quantity)}</td>
			<td>${escHtml(item.unit_name || item.unit_id || '—')}</td>
			<td>${escHtml(item.packaging_name || item.packaging_id || '—')}</td>
			<td>${escHtml(createdByName)}</td>
			<td>${fmtDt(item.created_at)}</td>
		</tr>`;
	}).join('');

	return `
		<table style="width:100%;border-collapse:collapse;font-size:0.88em;margin-top:0.25rem">
			<thead>
				<tr style="background:#f0f0f0">
					<th style="padding:4px 6px;text-align:left">Linha</th>
					<th style="padding:4px 6px;text-align:left">SKU</th>
					<th style="padding:4px 6px;text-align:left">Produto</th>
					<th style="padding:4px 6px;text-align:left">Lote</th>
					<th style="padding:4px 6px;text-align:right">Qtd.</th>
					<th style="padding:4px 6px;text-align:left">Unidade</th>
					<th style="padding:4px 6px;text-align:left">Embalagem</th>
					<th style="padding:4px 6px;text-align:left">Registrado por</th>
					<th style="padding:4px 6px;text-align:left">Registrado em</th>
				</tr>
			</thead>
			<tbody>
				${rows}
			</tbody>
		</table>
	`;
}

/**
 * Abre modal de detalhe de uma caixa de picking.
 *
 * @param {string} companyId
 * @param {number|string} outputId
 * @param {string} boxId
 * @param {function} [onCancel] - callback chamado após cancelamento bem-sucedido
 */
export default async function openPickingBoxDetailModal(companyId, outputId, boxId, onCancel) {
	// Carrega dados
	let boxData;
	try {
		const resp = await api.call(api.list_output_picking_boxes, companyId, outputId);
		const boxes = resp?.boxes || [];
		boxData = boxes.find(b => b.id === boxId || String(b.id) === String(boxId));
	} catch (e) {
		notify(e?.message || 'Erro ao carregar dados da caixa.', 'Erro');
		return;
	}

	if (!boxData) {
		notify('Caixa não encontrada.', 'Detalhe da Caixa');
		return;
	}

	const statusLabel = STATUS_BOX_LABEL[boxData.status] || boxData.status;
	const statusStyle = STATUS_BOX_STYLE[boxData.status]  || '';
	const canCancel   = boxData.status === 10 || boxData.status === 20;
	const canOperate  = boxData.status === 10;

	const items = boxData.items || [];

	const infoRows = [
		['Caixa',       escHtml(boxData.code)],
		['Lacre',       escHtml(boxData.seal_code || '—')],
		['Saída',       escHtml(boxData.output_id)],
		['Status',      `<span style="display:inline-block;padding:2px 8px;border-radius:3px;font-weight:600;font-size:0.9em;${statusStyle}">${escHtml(statusLabel)}</span>`],
		['Aberta por',  escHtml(boxData.opened_by_username || boxData.opened_by || '—')],
		['Aberta em',   fmtDt(boxData.opened_at)],
		['Lacrada por', escHtml(boxData.closed_by_username || boxData.closed_by || '—')],
		['Lacrada em',  fmtDt(boxData.closed_at)],
		['Cancelada por', escHtml(boxData.canceled_by_username || boxData.canceled_by || '—')],
		['Cancelada em',  fmtDt(boxData.canceled_at)],
		['Observações', escHtml(boxData.notes || '—')],
	].map(([label, val]) =>
		`<tr>
			<td style="padding:3px 8px 3px 0;font-weight:600;white-space:nowrap;color:#555">${label}</td>
			<td style="padding:3px 0">${val}</td>
		</tr>`
	).join('');

	const cancelButton = canCancel
		? `<button type="button" class="button mtl btn-do-cancel">Cancelar Caixa</button>`
		: '';
	const operateButtons = canOperate
		? `<button type="button" class="button mtl btn-do-add-items">Adicionar Itens</button>
		   <button type="button" class="button mtl btn-do-close">Lacrar Caixa</button>`
		: '';

	const html = `
		<div class="msgbox" style="min-width:520px;max-width:760px">
			<div class="mb-title">Detalhe da Caixa — ${escHtml(boxData.code)}</div>
			<div class="mb-text" style="max-height:75vh;overflow-y:auto">

				<table style="border-collapse:collapse;font-size:0.9em;margin-bottom:1rem">
					<tbody>${infoRows}</tbody>
				</table>

				<strong style="display:block;margin-bottom:0.4rem;font-size:0.9em">Itens da Caixa</strong>
				${buildItemsTable(items, {})}

			</div>
			${operateButtons}
			${cancelButton}
			<button type="button" class="button mtl bn_N">Fechar</button>
		</div>
	`;

	const dlg = dialog.create(html, (key) => {
		if (key === 'Escape' || key === 'N') dlg.destroy();
	}, /Escape|N/);

	const afterBoxOp = async () => {
		if (typeof onCancel === 'function') onCancel();
		await openPickingBoxDetailModal(companyId, outputId, boxId, onCancel);
	};

	if (canOperate) {
		const addBtn = dlg.getNode().querySelector('.btn-do-add-items');
		if (addBtn) {
			addBtn.addEventListener('click', () => {
				dlg.destroy();
				openAddItemsModal(companyId, outputId, boxId, boxData.code, afterBoxOp);
			});
		}
		const closeBtn = dlg.getNode().querySelector('.btn-do-close');
		if (closeBtn) {
			closeBtn.addEventListener('click', () => {
				dlg.destroy();
				openCloseBoxModal(companyId, outputId, boxId, boxData.code, items.length, afterBoxOp);
			});
		}
	}

	if (canCancel) {
		const cancelBtn = dlg.getNode().querySelector('.btn-do-cancel');
		if (cancelBtn) {
			cancelBtn.addEventListener('click', () => {
				dlg.destroy();
				openCancelConfirm(companyId, outputId, boxId, boxData.code, onCancel);
			});
		}
	}
}

/** Modal de confirmação do cancelamento da caixa. */
function openCancelConfirm(companyId, outputId, boxId, boxCode, onSuccess) {
	openCancelBoxModal(companyId, outputId, boxId, boxCode, onSuccess);
}
